import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const { t } = useTranslation();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLanguageChange = (language) => {
    changeLanguage(language);
  };

  return (
    <nav className="sticky top-0 z-50 bg-light-bg/90 backdrop-blur-md border-b border-muted-gold/20 shadow-lg">
      <div className="container-custom py-4 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="text-2xl font-heading font-bold text-text-dark tracking-wider text-shadow-sm hover:text-shadow-gold transition-all duration-300">
          LUCIEN14
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex space-x-8">
          <Link to="/#hero" className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide relative group">
            <span>{t('nav.home')}</span>
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-muted-gold transition-all duration-300 group-hover:w-full"></span>
          </Link>
          <Link to="/#collections" className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide relative group">
            <span>{t('nav.collections')}</span>
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-muted-gold transition-all duration-300 group-hover:w-full"></span>
          </Link>
          <Link to="/#culinary" className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide relative group">
            <span>{t('nav.culinary')}</span>
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-muted-gold transition-all duration-300 group-hover:w-full"></span>
          </Link>
          <Link to="/#menu" className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide relative group">
            <span>{t('nav.menu')}</span>
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-muted-gold transition-all duration-300 group-hover:w-full"></span>
          </Link>
          <Link to="/#events" className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide relative group">
            <span>{t('nav.events')}</span>
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-muted-gold transition-all duration-300 group-hover:w-full"></span>
          </Link>
          <Link to="/admin" className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide relative group">
            <span>{t('nav.admin')}</span>
            <span className="absolute -bottom-1 left-0 w-0 h-px bg-muted-gold transition-all duration-300 group-hover:w-full"></span>
          </Link>

          {/* Language Selector */}
          <div className="relative group">
            <button className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide flex items-center">
              <span className="uppercase">{currentLanguage}</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="absolute right-0 mt-2 w-32 bg-white shadow-lg rounded-md overflow-hidden transform scale-0 group-hover:scale-100 transition-transform origin-top-right duration-200 z-50">
              <button
                onClick={() => handleLanguageChange(languages.EN)}
                className={`w-full text-left px-4 py-2 text-sm ${currentLanguage === languages.EN ? 'bg-muted-gold/10 text-muted-gold' : 'text-text-dark hover:bg-muted-gold/5'}`}
              >
                {t('language.en')}
              </button>
              <button
                onClick={() => handleLanguageChange(languages.FR)}
                className={`w-full text-left px-4 py-2 text-sm ${currentLanguage === languages.FR ? 'bg-muted-gold/10 text-muted-gold' : 'text-text-dark hover:bg-muted-gold/5'}`}
              >
                {t('language.fr')}
              </button>
              <button
                onClick={() => handleLanguageChange(languages.ES)}
                className={`w-full text-left px-4 py-2 text-sm ${currentLanguage === languages.ES ? 'bg-muted-gold/10 text-muted-gold' : 'text-text-dark hover:bg-muted-gold/5'}`}
              >
                {t('language.es')}
              </button>
              <button
                onClick={() => handleLanguageChange(languages.TR)}
                className={`w-full text-left px-4 py-2 text-sm ${currentLanguage === languages.TR ? 'bg-muted-gold/10 text-muted-gold' : 'text-text-dark hover:bg-muted-gold/5'}`}
              >
                {t('language.tr')}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-muted-gold hover:text-text-dark focus:outline-none transition-all duration-300 relative group"
          onClick={toggleMenu}
        >
          <motion.div
            initial={false}
            animate={isOpen ? "open" : "closed"}
            variants={{
              open: { rotate: 90 },
              closed: { rotate: 0 }
            }}
            transition={{ duration: 0.3 }}
            className="relative"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
            <span className="absolute inset-0 rounded-full opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100" style={{ boxShadow: '0 0 8px rgba(201, 168, 106, 0.6)' }}></span>
          </motion.div>
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <motion.div
          className="md:hidden bg-light-bg-alt/95 backdrop-blur-md border-b border-muted-gold/20 shadow-lg"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="container-custom py-6 flex flex-col space-y-5">
            <Link
              to="/#hero"
              className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide pl-2 border-l-2 border-transparent hover:border-muted-gold/50 hover:pl-4"
              onClick={toggleMenu}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/#collections"
              className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide pl-2 border-l-2 border-transparent hover:border-muted-gold/50 hover:pl-4"
              onClick={toggleMenu}
            >
              {t('nav.collections')}
            </Link>
            <Link
              to="/#culinary"
              className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide pl-2 border-l-2 border-transparent hover:border-muted-gold/50 hover:pl-4"
              onClick={toggleMenu}
            >
              {t('nav.culinary')}
            </Link>
            <Link
              to="/#menu"
              className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide pl-2 border-l-2 border-transparent hover:border-muted-gold/50 hover:pl-4"
              onClick={toggleMenu}
            >
              {t('nav.menu')}
            </Link>
            <Link
              to="/#events"
              className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide pl-2 border-l-2 border-transparent hover:border-muted-gold/50 hover:pl-4"
              onClick={toggleMenu}
            >
              {t('nav.events')}
            </Link>
            <Link
              to="/admin"
              className="text-text-dark hover:text-muted-gold transition-all duration-300 tracking-wide pl-2 border-l-2 border-transparent hover:border-muted-gold/50 hover:pl-4"
              onClick={toggleMenu}
            >
              {t('nav.admin')}
            </Link>

            {/* Language Selector */}
            <div className="pt-2 mt-2 border-t border-muted-gold/10">
              <p className="text-xs text-muted-gold mb-2 pl-2">{t('language.select')}:</p>
              <div className="flex flex-wrap gap-2 pl-2">
                <button
                  onClick={() => {
                    handleLanguageChange(languages.EN);
                    toggleMenu();
                  }}
                  className={`px-3 py-1 rounded-md text-sm ${
                    currentLanguage === languages.EN 
                      ? 'bg-muted-gold/10 text-muted-gold' 
                      : 'text-text-dark hover:bg-muted-gold/5'
                  }`}
                >
                  {t('language.en')}
                </button>
                <button
                  onClick={() => {
                    handleLanguageChange(languages.FR);
                    toggleMenu();
                  }}
                  className={`px-3 py-1 rounded-md text-sm ${
                    currentLanguage === languages.FR 
                      ? 'bg-muted-gold/10 text-muted-gold' 
                      : 'text-text-dark hover:bg-muted-gold/5'
                  }`}
                >
                  {t('language.fr')}
                </button>
                <button
                  onClick={() => {
                    handleLanguageChange(languages.ES);
                    toggleMenu();
                  }}
                  className={`px-3 py-1 rounded-md text-sm ${
                    currentLanguage === languages.ES 
                      ? 'bg-muted-gold/10 text-muted-gold' 
                      : 'text-text-dark hover:bg-muted-gold/5'
                  }`}
                >
                  {t('language.es')}
                </button>
                <button
                  onClick={() => {
                    handleLanguageChange(languages.TR);
                    toggleMenu();
                  }}
                  className={`px-3 py-1 rounded-md text-sm ${
                    currentLanguage === languages.TR 
                      ? 'bg-muted-gold/10 text-muted-gold' 
                      : 'text-text-dark hover:bg-muted-gold/5'
                  }`}
                >
                  {t('language.tr')}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar;
